/** Small geometry helpers for map overlays (no dependency): circle outlines, shape outlines and distances. */
import type { MapShape } from './types';

const EARTH_M = 6_371_008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Circle as a closed ring of [lon, lat] points (geodesic offsets; fine for 25 m - 50 km). */
export function circleRing(center: { lat: number; lon: number }, radiusM: number, steps = 64): [number, number][] {
  const lat1 = rad(center.lat);
  const lon1 = rad(center.lon);
  const d = radiusM / EARTH_M;
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const b = (2 * Math.PI * i) / steps;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
    const lon2 = lon1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    out.push([deg(lon2), deg(lat2)]);
  }
  return out;
}

/** Outline points of a shape ([lon, lat]). */
export function shapePoints(s: MapShape, steps = 32): [number, number][] {
  return s.kind === 'circle' ? circleRing(s.center, s.radiusM, steps) : s.points;
}

/** Great-circle distance in metres. */
export function distanceM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Point in polygon (ray casting; [lon, lat] vertices, implicitly closed). */
export function pointInPolygon(p: { lat: number; lon: number }, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > p.lat !== yj > p.lat && p.lon < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
